import { Bell } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { formatDateTime } from '@/lib/format';
import {
  useMarkAllNotificationsRead,
  useMarkNotificationRead,
  useNotifications,
  useUnreadCount,
} from '../queries';
import type { NotificationDto } from '../types';
import { useUserChannel } from '../useUserChannel';

const PREVIEW_SIZE = 8;

/**
 * The notification centre's header affordance (backend ADR-019).
 *
 * This is where the realtime layer becomes visible: `useUserChannel()` binds
 * the caller's own `user:` channel, so the badge moves the moment the server
 * publishes — and the REST poll underneath keeps it right when the socket is
 * down or not deployed at all (FE-ADR-017).
 */
export function NotificationBell() {
  const { t } = useTranslation('notifications');
  const navigate = useNavigate();

  useUserChannel();

  const { data: count } = useUnreadCount();
  const { data: page, isLoading } = useNotifications({ page_size: PREVIEW_SIZE });
  const markRead = useMarkNotificationRead();
  const markAllRead = useMarkAllNotificationsRead();

  const unread = count?.unread ?? 0;
  const items = page?.results ?? [];

  const open = (notification: NotificationDto) => {
    if (!notification.is_read) markRead.mutate(notification.public_id);
    if (notification.link) navigate(notification.link);
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="relative"
          aria-label={unread > 0 ? t('unreadCount', { count: unread }) : t('title')}
        >
          <Bell />
          {unread > 0 && (
            <span
              aria-hidden="true"
              className="bg-destructive text-destructive-foreground absolute top-1 right-1 flex min-w-4 items-center justify-center rounded-full px-1 text-[10px] leading-4 font-semibold"
            >
              {unread > 99 ? '99+' : unread}
            </span>
          )}
        </Button>
      </DropdownMenuTrigger>

      <DropdownMenuContent align="end" className="w-80">
        <DropdownMenuLabel className="flex items-center justify-between gap-2">
          <span>{t('title')}</span>
          {unread > 0 && (
            <button
              type="button"
              className="text-muted-foreground hover:text-foreground text-xs font-normal underline"
              onClick={() => markAllRead.mutate()}
            >
              {t('markAllRead')}
            </button>
          )}
        </DropdownMenuLabel>
        <DropdownMenuSeparator />

        {isLoading && <div className="text-muted-foreground p-3 text-sm">{t('loading')}</div>}
        {!isLoading && items.length === 0 && (
          <div className="text-muted-foreground p-3 text-sm">{t('empty')}</div>
        )}

        {items.map(notification => (
          <DropdownMenuItem
            key={notification.public_id}
            className="flex-col items-start gap-0.5"
            onSelect={() => open(notification)}
          >
            <span className={notification.is_read ? 'text-sm' : 'text-sm font-semibold'}>
              {notification.title}
            </span>
            <span className="text-muted-foreground text-xs">
              {formatDateTime(notification.created)}
            </span>
          </DropdownMenuItem>
        ))}

        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={() => navigate('/notifications')}>
          {t('seeAll')}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
